import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Heart, Shield, ShieldCheck, X } from "lucide-react";
import { streakInfo, buyShield, reviveStreak } from "../lib/api";

/**
 * Streak protection actions for the mobile Profile page.
 *
 * Lives below the "Career ladder" section. Renders 0-2 `setrow`-style rows
 * matching the profile's existing UI vocabulary — no big panel, no flashy
 * counters. Just the failover (shield) and revive actions where the user
 * naturally looks for streak settings.
 *
 * Self-contained: fetches its own /streak/info, opens a confirmation modal,
 * calls the action, refreshes itself, and notifies the parent via onChange.
 */
export default function StreakProtectionRows({ onChange }) {
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind, cost, title, body }

    const load = useCallback(async () => {
        try {
            const { data } = await streakInfo();
            setInfo(data);
        } catch (err) {
            console.warn("[streak-protection] info fetch failed:", err?.message || err);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const askShield = () => setConfirm({
        kind: "shield",
        cost: info.shield_cost,
        title: "Arm Streak Shield?",
        body: `If you miss a single day, this shield is automatically consumed to keep your ${info.streak_count}-day streak alive.`,
    });

    const askRevive = () => setConfirm({
        kind: "revive",
        cost: info.revive_cost,
        title: `Revive ${info.lost_streak}-day streak?`,
        body: `Restores your previous streak of ${info.lost_streak} days. The revive window stays open for ${info.revive_window_days || 7} days after the break — ${info.revive_days_left ?? "?"} day${info.revive_days_left === 1 ? "" : "s"} left for this one.`,
    });

    const runConfirmed = async () => {
        const action = confirm.kind;
        setBusy(true);
        try {
            if (action === "shield") {
                await buyShield();
                toast.success("Streak Shield armed.");
            } else {
                const { data } = await reviveStreak();
                toast.success(`Streak restored to ${data.restored_to} days.`);
            }
            setConfirm(null);
            await load();
            if (onChange) onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    if (!info) return null;

    const { shield_active, shield_cost, can_revive, lost_streak, revive_cost, streak_count } = info;
    // Shield only becomes relevant once there's a streak worth protecting (3+ days).
    const offerShield = !shield_active && streak_count >= 3 && !!shield_cost;
    // Hide the whole section when nothing is actionable and no active shield to show.
    if (!shield_active && !can_revive && !offerShield) return null;

    return (
        <>
            <div className="m-sec"><span className="lab">Streak protection</span></div>
            <div className="setlist">
                {shield_active ? (
                    <div className="setrow" data-testid="shield-active-row">
                        <span className="sic"><ShieldCheck /></span>
                        <span className="sl">Shield armed</span>
                        <span className="sr" style={{ color: "var(--green)" }}>active</span>
                    </div>
                ) : offerShield ? (
                    <button
                        type="button"
                        className="setrow"
                        onClick={askShield}
                        disabled={busy}
                        data-testid="profile-buy-shield"
                    >
                        <span className="sic"><Shield /></span>
                        <span className="sl">Streak shield</span>
                        <span className="sr" style={{ color: "var(--gold)" }}>{shield_cost} coins</span>
                    </button>
                ) : null}

                {can_revive && (
                    <button
                        type="button"
                        className="setrow"
                        onClick={askRevive}
                        disabled={busy}
                        data-testid="profile-revive"
                    >
                        <span className="sic"><Heart /></span>
                        <span className="sl">Revive {lost_streak}-day streak</span>
                        <span className="sr" style={{ color: "var(--gold)" }}>{revive_cost} coins</span>
                    </button>
                )}
            </div>

            {confirm && (
                <div
                    className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={() => !busy && setConfirm(null)}
                    data-testid="streak-confirm-modal"
                >
                    <div
                        className="bg-[var(--card)] border border-[var(--bdr)] max-w-sm w-full p-5 relative max-h-[85vh] overflow-y-auto"
                        style={{ borderRadius: 14, paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setConfirm(null)}
                            disabled={busy}
                            aria-label="Close"
                            className="absolute top-3 right-3 text-[var(--tx-4)] hover:text-white"
                        >
                            <X size={18} />
                        </button>
                        <div className="eyebrow">Confirm</div>
                        <h3 className="heading text-xl uppercase font-black mt-1">{confirm.title}</h3>
                        <p className="text-sm text-[var(--tx-3)] mt-2 leading-relaxed">{confirm.body}</p>
                        <div className="mt-4 text-sm">
                            Cost: <span style={{ color: "var(--gold)", fontWeight: 700 }}>{confirm.cost} coins</span>
                        </div>
                        <div className="mt-5 flex gap-2">
                            <button
                                onClick={() => setConfirm(null)}
                                disabled={busy}
                                className="btn btn-ghost flex-1 disabled:opacity-40"
                                data-testid="streak-confirm-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={runConfirmed}
                                disabled={busy}
                                className="btn btn-primary flex-1 disabled:opacity-40"
                                data-testid="streak-confirm-go"
                            >
                                {busy ? "Working…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
